import { intersection, isEmpty } from 'lodash-es';
import isNil from 'lodash-es/isNil';
import noop from 'lodash-es/noop';
import { Resource } from '../Resource';
import { apiVersion } from '../apiVersion';
import { Universe } from '../core/universes';
import * as enums from '../enums';
import { fertigationMetrics } from '../enums/MetricIds';
import * as methods from '../methods';
import * as transformers from '../transformers';
import { fromRaw as fromRawMeasurementConfig, toRaw as toRawMeasurementConfig } from './measurementConfigs';
const rootPath = 'plots';
const soilMeshPath = 'soil_mesh';
// at this point this enum is equals to SoilTexture but this is subject to change
export var CapacitiveEquation;
(function (CapacitiveEquation) {
    CapacitiveEquation[CapacitiveEquation["default"] = 0] = "default";
    CapacitiveEquation[CapacitiveEquation["clay"] = 1] = "clay";
    CapacitiveEquation[CapacitiveEquation["siltyClay"] = 2] = "siltyClay";
    CapacitiveEquation[CapacitiveEquation["siltyClayLoam"] = 3] = "siltyClayLoam";
    CapacitiveEquation[CapacitiveEquation["sandyClay"] = 4] = "sandyClay";
    CapacitiveEquation[CapacitiveEquation["sandyClayLoam"] = 5] = "sandyClayLoam";
    CapacitiveEquation[CapacitiveEquation["clayLoam"] = 6] = "clayLoam";
    CapacitiveEquation[CapacitiveEquation["silt"] = 7] = "silt";
    CapacitiveEquation[CapacitiveEquation["siltLoam"] = 8] = "siltLoam";
    CapacitiveEquation[CapacitiveEquation["loam"] = 9] = "loam";
    CapacitiveEquation[CapacitiveEquation["sand"] = 10] = "sand";
    CapacitiveEquation[CapacitiveEquation["loamySand"] = 11] = "loamySand";
    CapacitiveEquation[CapacitiveEquation["sandyLoam"] = 12] = "sandyLoam";
})(CapacitiveEquation || (CapacitiveEquation = {}));
export var IrrigationType;
(function (IrrigationType) {
    IrrigationType[IrrigationType["sprinkle"] = 0] = "sprinkle";
    IrrigationType[IrrigationType["drip"] = 1] = "drip";
    IrrigationType[IrrigationType["other"] = 2] = "other";
    /** Not known, but explicitly selected, this is not the same as an empty {@link IrrigationType} */
    IrrigationType[IrrigationType["blank"] = -1] = "blank";
})(IrrigationType || (IrrigationType = {}));
export var SoilTexture;
(function (SoilTexture) {
    SoilTexture[SoilTexture["custom"] = 0] = "custom";
    SoilTexture[SoilTexture["clay"] = 1] = "clay";
    SoilTexture[SoilTexture["siltClay"] = 2] = "siltClay";
    SoilTexture[SoilTexture["siltyClayLoam"] = 3] = "siltyClayLoam";
    SoilTexture[SoilTexture["sandyClay"] = 4] = "sandyClay";
    SoilTexture[SoilTexture["sandyClayLoam"] = 5] = "sandyClayLoam";
    SoilTexture[SoilTexture["clayLoam"] = 6] = "clayLoam";
    SoilTexture[SoilTexture["silt"] = 7] = "silt";
    SoilTexture[SoilTexture["siltLoam"] = 8] = "siltLoam";
    SoilTexture[SoilTexture["loam"] = 9] = "loam";
    SoilTexture[SoilTexture["sand"] = 10] = "sand";
    SoilTexture[SoilTexture["loamySand"] = 11] = "loamySand";
    SoilTexture[SoilTexture["sandyLoam"] = 12] = "sandyLoam";
})(SoilTexture || (SoilTexture = {}));
export var EnvironmentType;
(function (EnvironmentType) {
    EnvironmentType[EnvironmentType["openField"] = 0] = "openField";
    EnvironmentType[EnvironmentType["greenHouse"] = 1] = "greenHouse";
})(EnvironmentType || (EnvironmentType = {}));
export var SubstrateType;
(function (SubstrateType) {
    SubstrateType[SubstrateType["mineral"] = 0] = "mineral";
    SubstrateType[SubstrateType["organic"] = 1] = "organic";
    SubstrateType[SubstrateType["peatMix"] = 2] = "peatMix";
    SubstrateType[SubstrateType["coir"] = 3] = "coir";
    SubstrateType[SubstrateType["mineralWood"] = 4] = "mineralWood";
})(SubstrateType || (SubstrateType = {}));
export const fromRawPlotToPlot = ({ active_options, archived, available_alert_types, available_measures, available_universes, created_at, current_measurement_configs, favorite, id, location, location_text, meta, my_role, name, org_devices, organization, organization_name, reactivable_options, shape, soil_texture_pct, soil_texture, tzinfo, virtual_device }) => {
    return {
        kind: 'plot',
        activeOptions: active_options.sort((a, b) => a.localeCompare(b)),
        archived: archived,
        availableAlertTypes: available_alert_types,
        availableMeasures: available_measures,
        createdAt: created_at,
        currentMeasurementConfigs: current_measurement_configs
            ? current_measurement_configs.map(fromRawMeasurementConfig)
            : [],
        deviceIds: org_devices,
        hasVirtualDevice: !!virtual_device,
        id,
        location,
        meta: {
            irrigationType: meta?.irrigation_type,
            environmentType: meta?.environment_type,
            substrateType: meta?.substrate,
            grapeVisionId: meta?.[enums.OptionCode.grapeVision]?.grapevision_id,
            capacitiveEquation: meta?.capa_equation,
            thresholdsProfileId: meta?.custom_threshold
        },
        myRole: my_role,
        name,
        organizationId: organization,
        organizationName: organization_name,
        reactivableOptions: reactivable_options,
        reverseGeocoding: location_text,
        shape,
        soilTexture: soil_texture,
        soilTextureComposition: soil_texture_pct,
        timezone: tzinfo,
        isFavorite: favorite,
        // Hoping this is a temporary workaround and that the backend will add Universe.weather itself rather than the front ...
        availableUniverses: !isNil(available_universes) && !available_universes.includes(Universe.weather)
            ? [Universe.weather, ...available_universes]
            : !isNil(available_universes)
                ? available_universes
                : undefined
    };
};
export const fromRaw = {
    isFavorite: ({ favorite }) => ({
        isFavorite: favorite
    }),
    get: fromRawPlotToPlot,
    getSoilMesh: ({ soil_texture }) => {
        return { soilTexture: soil_texture };
    }
};
export const hasFertigationMetric = (plot) => !isEmpty(intersection(plot.availableMeasures, fertigationMetrics));
const toRaw = {
    archive: () => ({}),
    create: ({ name, organizationId, shape, reverseGeocoding, deviceIds, hasVirtualDevice, horizons }) => ({
        name: name.trim(),
        organization: organizationId,
        shape,
        location_text: reverseGeocoding,
        org_devices: deviceIds,
        virtual_device: hasVirtualDevice,
        horizons
    }),
    createFromStation: ({ deviceId, stationId }) => ({
        device_id: deviceId,
        station_id: stationId
    }),
    update: ({ activeOptions, archived, availableMeasures, currentMeasurementConfigs, meta, myRole, name, organizationId, reverseGeocoding, shape, soilTexture, soilTextureComposition, timezone }) => {
        let finalMeta = undefined;
        // Not sending custom_threshold on purpose here
        // Otherwise it returns an error : Unsupported extra fields: {'custom_threshold'}
        if (!isNil(meta)) {
            finalMeta = {};
            if (!isNil(meta.irrigationType)) {
                finalMeta.irrigation_type = meta.irrigationType;
            }
            if (!isNil(meta.capacitiveEquation)) {
                finalMeta.capa_equation = meta.capacitiveEquation;
            }
            if (!isNil(meta.environmentType)) {
                finalMeta.environment_type = meta.environmentType;
            }
            if (!isNil(meta.substrateType)) {
                finalMeta.substrate = meta.substrateType;
            }
            // Do not add custom_threshold or grapevision_id, update this way is not supported
        }
        return {
            active_options: activeOptions,
            archived,
            available_measures: availableMeasures,
            current_measurement_configs: currentMeasurementConfigs
                ? currentMeasurementConfigs.map(toRawMeasurementConfig)
                : undefined,
            my_role: myRole,
            name,
            organization: organizationId,
            shape: shape,
            soil_texture: soilTexture,
            tzinfo: timezone,
            soil_texture_pct: soilTextureComposition,
            location_text: reverseGeocoding,
            ...(finalMeta ? { meta: { ...finalMeta } } : {})
        };
    },
    updateDevices: ({ hasVirtualDevice, deviceIds }) => ({
        virtual_device: hasVirtualDevice,
        org_devices: deviceIds
    }),
    updateDeviceConfig: ({ deviceId, start }) => ({
        org_device: deviceId,
        start
    })
};
const cacheConfig = {
    staleTime: 1 * 60 * 1000
};
export const plotMutationExternalInvalidationKeys = new Set();
const plots = Resource((requesterBuilder) => {
    const getMany = (query) => requesterBuilder({
        path: rootPath,
        requestCtx: {
            ...apiVersion[1.1],
            transform: transformers.paginatedResponse(fromRaw.get),
            params: {
                query: {
                    available_options: query?.availableOptions,
                    limit: query?.limit,
                    universe: query?.universe,
                    offset: query?.offset,
                    id__in: !isNil(query?.ids) ? query?.ids.join(',') : undefined,
                    metrics__in: query?.metrics,
                    metrics_op: query?.metricOperator,
                    with_shape: query?.withShape,
                    q: query?.q,
                    archived: query?.isArchived,
                    organization: query?.organizationId,
                    virtual_device: query?.hasVirtualDevice,
                    favorite: query?.isFavorite
                }
            }
        },
        config: cacheConfig
    });
    const get = methods.get({
        requesterBuilder,
        rootPath,
        fromRaw: fromRaw.get,
        cacheConfig,
        ...apiVersion[1.1]
    });
    const getSoilMesh = (plotId) => requesterBuilder({
        path: soilMeshPath,
        requestCtx: {
            ...apiVersion[1.1],
            transform: fromRaw.getSoilMesh,
            params: {
                query: {
                    plot: plotId
                }
            }
        }
    });
    const getManyInvalidationKey = getMany().invalidationKey;
    const buildKeysToInvalidateOnCreate = () => [
        getManyInvalidationKey,
        ...Array.from(plotMutationExternalInvalidationKeys).map((k) => [k])
    ];
    return {
        create: methods.create({
            requesterBuilder,
            rootPath,
            fromRaw: fromRaw.get,
            toRaw: toRaw.create,
            buildKeysToInvalidate: buildKeysToInvalidateOnCreate
        }),
        createFromStation: methods.create({
            requesterBuilder,
            rootPath: `${rootPath}/from-station`,
            fromRaw: fromRaw.get,
            toRaw: toRaw.createFromStation,
            buildKeysToInvalidate: buildKeysToInvalidateOnCreate
        }),
        get,
        getMany,
        getPage: methods.getPage({
            getMany
        }),
        setFavorite: (plotId) => requesterBuilder({
            path: `${rootPath}/${plotId}/set_favorite`,
            requestCtx: {
                transform: fromRaw.isFavorite,
                method: 'POST'
            },
            keysToInvalidate: [...buildKeysToInvalidateOnCreate(), get(plotId).key]
        }),
        unsetFavorite: (plotId) => requesterBuilder({
            path: `${rootPath}/${plotId}/unset_favorite`,
            requestCtx: {
                transform: fromRaw.isFavorite,
                method: 'POST'
            },
            keysToInvalidate: [...buildKeysToInvalidateOnCreate(), get(plotId).key]
        }),
        update: methods.update({
            requesterBuilder,
            rootPath,
            fromRaw: fromRaw.get,
            toRaw: toRaw.update,
            method: 'PATCH',
            ...apiVersion[1.1],
            buildKeysToInvalidate: (id) => {
                return [...buildKeysToInvalidateOnCreate(), get(id).key];
            }
        }),
        delete: methods.deletion({
            requesterBuilder,
            rootPath,
            buildKeysToInvalidate: (id) => {
                return [...buildKeysToInvalidateOnCreate(), get(id).key];
            }
        }),
        archive: (plotId) => methods.update({
            method: 'POST',
            rootPath: `${rootPath}/${plotId}/archive`,
            toRaw: toRaw.archive,
            fromRaw: fromRaw.get,
            requesterBuilder,
            buildKeysToInvalidate: () => [...buildKeysToInvalidateOnCreate(), get(plotId).key]
        })(),
        updateDevices: (plotId) => methods.update({
            method: 'POST',
            rootPath: `${rootPath}/${plotId}/change_devices`,
            toRaw: toRaw.updateDevices,
            fromRaw: fromRaw.get,
            requesterBuilder,
            buildKeysToInvalidate: () => [
                ...buildKeysToInvalidateOnCreate(),
                get(plotId).invalidationKey
            ]
        })(),
        updateDeviceConfig: (plotId) => methods.update({
            method: 'PUT',
            rootPath: `${rootPath}/${plotId}/update_config`,
            toRaw: toRaw.updateDeviceConfig,
            fromRaw: noop,
            requesterBuilder,
            buildKeysToInvalidate: () => [
                ...buildKeysToInvalidateOnCreate(),
                get(plotId).invalidationKey
            ],
            patternToInvalidate: plotId
        })(),
        getAllPages: methods.getAllPages({ getMany }),
        getSoilMesh
    };
});
export default plots;
